import {
  arrow,
  ArrowOptions,
  autoUpdate,
  Delay,
  flip,
  FlipOptions,
  FloatingArrow,
  FloatingPortal,
  Middleware,
  offset,
  Placement,
  safePolygon,
  shift,
  ShiftOptions,
  size,
  SizeOptions,
  useClientPoint,
  UseClientPointProps,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles
} from "@floating-ui/react";
import classNames from "classnames";
import isNil from "lodash/isNil";
import noop from "lodash/noop";
import pickBy from "lodash/pickBy";
import values from "lodash/values";
import { Typography } from "PFComponents/typography";
import { CSSProperties, HTMLProps, ReactNode, Ref, useRef, useState } from "react";

import css from "./tooltip.module.scss";

const ARROW_PADDING = 8;
const TOOLTIP_OFFSET = 10;
const TOOLTIP_MARGIN = 8;
const TOOLTIP_MAX_WIDTH = 350;

type MiddlewareOptions<T> = { disabled?: boolean; custom?: Middleware } & Partial<T>;
type TooltipContainerProps = Omit<HTMLProps<HTMLDivElement>, "style"> & {
  style?: Omit<CSSProperties, "maxWidth"> & { maxWidth?: number };
};

export type TooltipProps<TriggerElement extends HTMLElement> = {
  children:
    | ReactNode
    | ((props: {
        ref: Ref<TriggerElement>;
        getReferenceProps: (props?: HTMLProps<Element>) => Record<string, unknown>;
      }) => ReactNode);
  triggerRef?: Ref<TriggerElement>;
  content?: ReactNode;
  placement?: Placement;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerContainerProps?: HTMLProps<HTMLDivElement>;
  /* To set maxWidth, use `tooltipContainerProps.style.maxWidth` which should be a number (pixels) */
  tooltipContainerProps?: TooltipContainerProps;
  clientPointAxis?: UseClientPointProps["axis"];
  middlewareProps?: {
    flip?: MiddlewareOptions<FlipOptions>;
    shift?: MiddlewareOptions<ShiftOptions>;
    arrow?: MiddlewareOptions<ArrowOptions>;
    size?: MiddlewareOptions<SizeOptions>;
  };
  floatingProps?: Partial<UseFloatingOptions>;
  interactive?: boolean;
  delay?: Delay;
  offset?: number;
};

const TooltipComponent = <TriggerElement extends HTMLElement = HTMLDivElement>({
  triggerRef,
  children,
  content,
  placement: preferredPlacement = "top",
  disabled,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  triggerContainerProps = {},
  tooltipContainerProps = {},
  clientPointAxis,
  middlewareProps = {},
  floatingProps = {},
  interactive,
  delay,
  offset: customOffset
}: TooltipProps<TriggerElement>) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const tooltipMaxWidth = tooltipContainerProps?.style?.maxWidth ?? TOOLTIP_MAX_WIDTH;

  const middleware = values(
    pickBy(
      {
        offset: offset(customOffset ?? TOOLTIP_OFFSET),
        flip: middlewareProps?.flip?.custom ?? flip(middlewareProps?.flip ?? {}),
        shift:
          middlewareProps?.shift?.custom ??
          shift({ padding: TOOLTIP_MARGIN, ...(middlewareProps?.shift ?? {}) }),
        size: size({
          apply({ availableWidth, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: availableWidth < tooltipMaxWidth ? `${availableWidth}px` : `${tooltipMaxWidth}px`
            });
          },
          padding: TOOLTIP_MARGIN,
          ...(middlewareProps?.size ?? {})
        }),
        arrow: arrow({ element: arrowRef, padding: ARROW_PADDING, ...(middlewareProps?.arrow ?? {}) })
      },
      (value, key) => !middlewareProps?.[key]?.disabled
    )
  );

  const { refs, context, floatingStyles } = useFloating({
    open: disabled ? false : open,
    onOpenChange: disabled ? noop : setOpen,
    placement: preferredPlacement,
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true
      }),
    middleware,
    ...floatingProps
  });
  const ref = useMergeRefs<TriggerElement>([refs.setReference, triggerRef]);

  const clientPoint = useClientPoint(context, { enabled: !!clientPointAxis, axis: clientPointAxis });
  const hover = useHover(context, {
    move: false,
    enabled: !disabled && isNil(controlledOpen),
    handleClose: interactive ? safePolygon() : undefined,
    delay
  });
  const focus = useFocus(context, {
    enabled: !disabled && isNil(controlledOpen)
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint, hover, focus, dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context);

  if (!content) {
    return typeof children === "function" ? children({ ref, getReferenceProps }) : children;
  }

  const isArrowVisible = !middlewareProps?.arrow?.disabled;
  return (
    <>
      {typeof children === "function" ? (
        children({ ref, getReferenceProps })
      ) : (
        <div ref={ref as Ref<HTMLDivElement>} {...getReferenceProps(triggerContainerProps)}>
          {children}
        </div>
      )}
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps({
              ...tooltipContainerProps,
              style: {
                maxWidth: tooltipMaxWidth,
                ...tooltipContainerProps?.style,
                ...floatingStyles,
                ...transitionStyles
              },
              className: classNames(css.tooltipContainer, tooltipContainerProps?.className, {
                [css.interactive]: !!interactive
              })
            })}
          >
            {typeof content === "string" ? <Typography variant="bodyRegular">{content}</Typography> : content}
            {isArrowVisible && <FloatingArrow context={context} ref={arrowRef} fill="var(--paletteBlue1)" />}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export const Tooltip = <TriggerElement extends HTMLElement = HTMLDivElement>({
  children,
  ...props
}: TooltipProps<TriggerElement>) => (
  // the key forces remount as useClientPoint doesn’t refresh reference on clientPointAxis change
  <TooltipComponent key={`tooltip-clientPointAxis-${props.clientPointAxis}`} {...props}>
    {children}
  </TooltipComponent>
);
